import type { PeriodKind } from "../../../shared/src";

// The analytics API only returns buckets that contain finished workouts, so a
// break in training would otherwise collapse on the x-axis (e.g. 29 Jun sitting
// right next to 24 Aug). Charts expand the returned starts into one bucket per
// calendar week/month so every bar spans exactly one period.

function nextPeriodStart(periodStart: string, period: PeriodKind): string {
  const d = new Date(`${periodStart}T00:00:00Z`);
  if (period === "monthly") {
    d.setUTCMonth(d.getUTCMonth() + 1, 1);
  } else {
    d.setUTCDate(d.getUTCDate() + 7);
  }
  return d.toISOString().slice(0, 10);
}

// Bars shown per dashboard chart; matches the `limit` the Dashboard requests.
export const CHART_BUCKETS = 8;

/**
 * Every period start from the earliest to the latest of `starts` (inclusive),
 * oldest-first, keeping only the most recent `maxBuckets`.
 */
export function continuousPeriodStarts(
  starts: string[],
  period: PeriodKind,
  maxBuckets: number = CHART_BUCKETS,
): string[] {
  if (starts.length === 0) return [];
  const sorted = [...starts].sort();
  const first = sorted[0] as string;
  const last = sorted[sorted.length - 1] as string;
  if (Number.isNaN(new Date(`${first}T00:00:00Z`).getTime())) return sorted;
  const out: string[] = [];
  for (let s = first; s <= last; s = nextPeriodStart(s, period)) {
    out.push(s);
  }
  return out.slice(Math.max(0, out.length - maxBuckets));
}

/**
 * Axis labels for a run of period starts. Monthly labels are the month name
 * alone, with the full year added only on the first bar and on January, so
 * "May 26" can't be misread as the 26th of May. Weekly labels are the ISO
 * week number and the number of the month holding most of that week, e.g.
 * "W39 9".
 */
export function periodLabels(starts: string[], period: PeriodKind): string[] {
  return starts.map((start, i) => {
    const d = new Date(`${start}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return start;
    if (period === "monthly") {
      const month = d.toLocaleDateString(undefined, {
        month: "short",
        timeZone: "UTC",
      });
      const showYear = i === 0 || d.getUTCMonth() === 0;
      return showYear ? `${month} ${d.getUTCFullYear()}` : month;
    }
    const { week, month } = isoWeekOf(d);
    return `W${week} ${month}`;
  });
}

// A Monday-start week belongs to the month and ISO year of its Thursday:
// four of its seven days always fall in that month.
function isoWeekOf(monday: Date): { week: number; month: number } {
  const thursday = new Date(monday);
  thursday.setUTCDate(monday.getUTCDate() + 3);
  const jan1 = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  const dayOfYear = Math.floor((thursday.getTime() - jan1) / 86_400_000);
  return {
    week: Math.floor(dayOfYear / 7) + 1,
    month: thursday.getUTCMonth() + 1,
  };
}

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}

// Stacks "W39 9" / "May 2026" onto two lines so every bar can carry its own
// label on a phone-width chart without Recharts dropping ticks.
export function PeriodTick({ x = 0, y = 0, payload }: TickProps) {
  const [first, ...rest] = (payload?.value ?? "").split(" ");
  return (
    <text x={x} y={y} textAnchor="middle" fill="#94a3b8" fontSize={10}>
      <tspan x={x} dy="0.71em">
        {first}
      </tspan>
      {rest.length > 0 && (
        <tspan x={x} dy="1.2em">
          {rest.join(" ")}
        </tspan>
      )}
    </text>
  );
}
